/**
 * ProtectedRoute Component
 * Redirects to login if user is not authenticated
 * Optionally checks for admin role
 */

import { Navigate, Outlet } from 'react-router-dom';
import { hasAdminAccess } from '../../lib/roles';
import { useAuthStore } from '../../stores/auth.store';

interface ProtectedRouteProps {
  requireAdmin?: boolean;
}

export const ProtectedRoute = ({ requireAdmin = false }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading, user } = useAuthStore();

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-meta">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check admin requirement
  if (requireAdmin && !hasAdminAccess(user?.role)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 dark:text-red-400">Access Denied</h1>
          <p className="mt-2 text-meta">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // Render child routes
  return <Outlet />;
};
